/* import __COLOCATED_TEMPLATE__ from './zendesk-deprecation.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type Folder from 'embercom/models/content-service/folder';
import type SyncSourceWrapper from 'embercom/models/knowledge-hub/sync-source-wrapper';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';
import type AppService from 'embercom/services/app-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';

interface Args {
  folder?: Folder;
}

interface Signature {
  Args: Args;
}

export default class ZendeskDeprecation extends Component<Signature> {
  @service declare appService: AppService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare permissionsService: any;
  @service declare notificationsService: any;

  @tracked showMigrateModal = false;
  @tracked migrateModalView: any = null;

  get shouldShow(): boolean {
    if (
      !this.appService.app.canUseFeature('team-knowledge-foundations-zendesk-deprecation-banner')
    ) {
      return false;
    }
    if (this.args.folder) {
      return this.isFolderDeprecated;
    }
    return this.deprecatedSources.length > 0;
  }

  private get isFolderDeprecated(): boolean {
    let folder = this.args.folder;
    if (!folder?.isSynced || folder?.parentFolder || folder?.sourceTypeName !== 'zendesk') {
      return false;
    }
    return !folder?.source || this.deprecatedSourceForFolder !== undefined;
  }

  get deprecatedSources(): SyncSourceWrapper[] {
    return this.knowledgeHubService.deprecatedZendeskSources;
  }

  @action
  async openMigrateModal() {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e: unknown) {
      // permissionsService.checkPermission displays its own error modal
      return;
    }

    let syncSource = this.args.folder ? this.deprecatedSourceForFolder : this.deprecatedSources[0];
    let sourceId = syncSource?.sourceId?.toString() || this.args.folder?.sourceId?.toString();

    if (!sourceId) {
      return;
    }

    try {
      let syncSetting = syncSource?.isDeprecated
        ? null
        : await this.store.findRecord('articles/article-sync-setting', sourceId).catch(() => null);

      let siteUrl = syncSetting?.siteUrl || syncSource?.siteUrl;
      if (!siteUrl) {
        throw new Error('Sync setting not found or missing site URL');
      }

      this.migrateModalView = {
        title: this.intl.t('knowledge-hub.sync-modal.zendesk.migration-title'),
        component: 'knowledge-hub/setup-modal/sync-modal/zendesk',
        isMigrating: true,
        existingSyncSetting: syncSetting ?? { siteUrl },
      };

      this.showMigrateModal = true;
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.zendesk.migration-failed'),
      );
    }
  }

  @action
  closeMigrateModal() {
    this.showMigrateModal = false;
    this.migrateModalView = null;
  }

  private get deprecatedSourceForFolder(): SyncSourceWrapper | undefined {
    let folder = this.args.folder;
    if (!folder) {
      return undefined;
    }
    return this.deprecatedSources.find(
      (source) =>
        (folder.sourceId && source.sourceId?.toString() === folder.sourceId?.toString()) ||
        (folder.id && source.folderId?.toString() === folder.id?.toString()),
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::Banners::ZendeskDeprecation': typeof ZendeskDeprecation;
    'knowledge-hub/banners/zendesk-deprecation': typeof ZendeskDeprecation;
  }
}
